import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { AuthService } from 'app/services/auth/auth.service';
import { FeatureFlagsService } from '../services/feature-flags/feature-flags.service';
import { FEATURE_FLAG_HOME } from '../services/feature-flags/feature-flags.constants';
import { DEO_AI_ASSIST_URL, HOME_URL, LandingRouteService } from '../services/landing-route/landing-route.service';

/**
 * Guard for the login path that redirects authenticated users to home, or to DEO AI Assist when
 * that is all they are entitled to. Allows unauthenticated users to access the login page.
 */
@Injectable({ providedIn: 'root' })
export class LoginRedirectGuard implements CanActivate {
  constructor(
    private readonly _authService: AuthService,
    private readonly _router: Router,
    private readonly _featureFlagsService: FeatureFlagsService,
    private readonly _landingRouteService: LandingRouteService
  ) {}

  async canActivate(): Promise<boolean | UrlTree> {
    // Required, do not remove: initial navigation and the auth app initializer run
    // concurrently (see appConfig), so this await is what makes routing wait for auth
    // to settle. Without it the guard reads an unauthenticated service on cold start.
    await this._authService.ready;

    // If user has active SSO session, redirect to their landing route
    if (this._authService.isLoggedIn()) {
      await this._featureFlagsService.ensureFeatureFlagsLoaded();

      if (!this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_HOME)) {
        return this._router.parseUrl(this._landingRouteService.resolveLandingUrl());
      }

      if (this._landingRouteService.isEntitledToDeoOnly()) {
        return this._router.parseUrl(DEO_AI_ASSIST_URL);
      }

      return this._router.parseUrl(HOME_URL);
    }

    // Otherwise, allow access to login page
    return true;
  }
}
